import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useSnackbar } from "_shared/_providers/snackbars/snackbars";
import {
  useDeleteSavedSearchMutation,
  useListSavedSearchesQuery,
  useUpdateSavedSearchMutation,
} from "marketplace/saved-searches/_api/saved-search-api";
import SavedSearchSchema from "marketplace/saved-searches/_schema/saved-search-schema";
import { savedSearchFailureText } from "marketplace/saved-searches/_views/saved-search-failure-text";
import SavedSearch from "marketplace/saved-searches/saved-search";
import React from "react";
import { Link as RouterLink } from "react-router";

const PageContainer = styled("div")(({ theme }) => ({
  margin: "0 auto",
  maxWidth: 900,
  padding: theme.spacing(3, 2),
}));

const RowActions = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexShrink: 0,
  gap: theme.spacing(0.5),
}));

const dateFormat = new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });

/** The user's saved marketplace searches: open, rename, delete, and turn alerts on or off. */
export default function SavedSearchesPage() {
  const { data, isLoading, isError } = useListSavedSearchesQuery();
  const savedSearches = React.useMemo(() => (data ?? []).map((json) => SavedSearch.fromJSON(json)), [data]);
  const [renaming, setRenaming] = React.useState<SavedSearch | null>(null);

  return (
    <PageContainer>
      <Typography variant="h5" component="h1" gutterBottom>
        Saved Searches
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Save a search from the marketplace to run it again. With alerts on, we tell you when a new profile
        matches it.
      </Typography>
      {isLoading && <CircularProgress aria-label="Loading saved searches" />}
      {isError && <Alert severity="error">Couldn&apos;t load your saved searches. Please try again.</Alert>}
      {!isLoading && !isError && savedSearches.length === 0 && (
        <Alert severity="info">
          You have no saved searches. Search the{" "}
          <Link component={RouterLink} to="/marketplace">
            marketplace
          </Link>{" "}
          and choose Save Search.
        </Alert>
      )}
      {savedSearches.length > 0 && (
        <Paper variant="outlined">
          {/* No list padding and no divider under the last row: the card's own border closes it, and
              either one drew an empty band below the last search. */}
          <List aria-label="Saved searches" disablePadding>
            {savedSearches.map((savedSearch, index) => (
              <SavedSearchRow
                key={savedSearch.id.value}
                savedSearch={savedSearch}
                divider={index < savedSearches.length - 1}
                onRename={setRenaming}
              />
            ))}
          </List>
        </Paper>
      )}
      {renaming && <RenameDialog savedSearch={renaming} onClose={() => setRenaming(null)} />}
    </PageContainer>
  );
}

function SavedSearchRow(
  props: Readonly<{ savedSearch: SavedSearch; divider: boolean; onRename: (savedSearch: SavedSearch) => void }>,
) {
  const { savedSearch, divider, onRename } = props;
  const { enqueueSnackbar } = useSnackbar();
  const confirm = useConfirmDialog();
  const [updateSavedSearch, { isLoading: isUpdating }] = useUpdateSavedSearchMutation();
  const [deleteSavedSearch, { isLoading: isDeleting }] = useDeleteSavedSearchMutation();

  async function handleAlertsChanged(alertsEnabled: boolean) {
    await updateSavedSearch({ savedSearchId: savedSearch.id.value, name: savedSearch.name, alertsEnabled })
      .unwrap()
      .catch((error: unknown) =>
        enqueueSnackbar(savedSearchFailureText(error, "Couldn't change the alerts. Please try again."), {
          variant: "error",
        }),
      );
  }

  async function handleDelete() {
    const response = await confirm({
      title: "Delete Saved Search?",
      message: `Delete "${savedSearch.name}"? You will no longer be told about new matches for it.`,
      okButtonText: "Delete",
      cancelButtonText: "Cancel",
    });
    if (response === ConfirmResponse.Cancel) return;

    await deleteSavedSearch({ savedSearchId: savedSearch.id.value })
      .unwrap()
      .then(() => enqueueSnackbar("Deleted saved search", { variant: "success" }))
      .catch((error: unknown) =>
        enqueueSnackbar(savedSearchFailureText(error, "Couldn't delete the saved search. Please try again."), {
          variant: "error",
        }),
      );
  }

  return (
    <ListItem divider={divider}>
      <ListItemText
        primary={
          <Link component={RouterLink} to={savedSearch.marketplaceUrl}>
            {savedSearch.name}
          </Link>
        }
        secondary={`Saved ${dateFormat.format(savedSearch.createdDate)}`}
      />
      <RowActions>
        <FormControlLabel
          control={
            <Switch
              checked={savedSearch.alertsEnabled}
              disabled={isUpdating}
              onChange={(event) => handleAlertsChanged(event.target.checked)}
            />
          }
          label="Alerts"
        />
        <Tooltip title="Rename">
          <IconButton aria-label={`Rename ${savedSearch.name}`} onClick={() => onRename(savedSearch)}>
            <EditOutlinedIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton aria-label={`Delete ${savedSearch.name}`} disabled={isDeleting} onClick={handleDelete}>
            <DeleteOutlineIcon />
          </IconButton>
        </Tooltip>
      </RowActions>
    </ListItem>
  );
}

function RenameDialog(props: Readonly<{ savedSearch: SavedSearch; onClose: () => void }>) {
  const { savedSearch, onClose } = props;
  const { enqueueSnackbar } = useSnackbar();
  const [updateSavedSearch, { isLoading }] = useUpdateSavedSearchMutation();
  const [name, setName] = React.useState(savedSearch.name);
  const trimmedName = name.trim();
  const nameTooLong = trimmedName.length > SavedSearchSchema.MaximumNameLength;

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!trimmedName || nameTooLong) return;

    const renamed = await updateSavedSearch({
      savedSearchId: savedSearch.id.value,
      name: trimmedName,
      alertsEnabled: savedSearch.alertsEnabled,
    })
      .unwrap()
      .then(() => true)
      .catch((error: unknown) => {
        enqueueSnackbar(savedSearchFailureText(error, "Couldn't rename the saved search. Please try again."), {
          variant: "error",
        });
        return false;
      });
    if (renamed) onClose();
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <form onSubmit={handleSubmit} noValidate>
        <DialogTitle>Rename saved search</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            error={nameTooLong}
            helperText={nameTooLong ? `Use ${SavedSearchSchema.MaximumNameLength} characters or fewer.` : undefined}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!trimmedName || nameTooLong || isLoading}>
            Rename
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
